import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Database, Workflow, Shield, Zap, Users, CheckCircle, ArrowRight, Link, Globe, Settings } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';
import { HolographicCard } from '@/components/cards/HolographicCard';

const EHRWorkflowIntegrations = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  
  const integrationFeatures = [
    {
      icon: Database,
      title: "Universal EHR Connectivity",
      description: "Connect seamlessly with Epic, Cerner, Allscripts, and 100+ other EHR systems through standardized APIs.",
      number: "01"
    },
    {
      icon: Workflow,
      title: "Adaptive Workflows",
      description: "AI-powered workflows that automatically adjust to your existing clinical processes and documentation patterns.",
      number: "02"
    },
    {
      icon: Shield,
      title: "HIPAA-Compliant Security",
      description: "Enterprise-grade security with end-to-end encryption, audit trails, and SOC 2 Type II compliance.",
      number: "03"
    },
    {
      icon: Zap,
      title: "Real-Time Synchronization",
      description: "Instant data synchronization ensures patient information is always current across all integrated systems.",
      number: "04"
    },
    {
      icon: Users,
      title: "Multi-Provider Support",
      description: "Supports complex healthcare organizations with multiple providers, specialties, and locations.",
      number: "05"
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: "Reduced Documentation Time by 40%",
      description: "Automated data entry and smart templates reduce administrative burden on clinical staff."
    },
    {
      icon: Zap,
      title: "10x Faster Implementation",
      description: "Pre-built integrations and configuration templates enable rapid deployment across your organization."
    },
    {
      icon: Shield,
      title: "99.9% Enhanced Data Security",
      description: "Industry-leading security protocols protect sensitive patient data with 99.9% uptime guarantee."
    }
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < integrationFeatures.length - 1) {
      setTimeout(() => setTypingCardIndex(index + 1), 500);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => setTypingCardIndex(0), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>EHR Workflow Integrations - AI-Powered Healthcare Platform | Canary</title>
        <meta name="description" content="Seamlessly integrate with your existing EHR and practice management systems. Built from real-world healthcare data spanning millions of patient encounters." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <NeuralBackground />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8">
                <Link className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">EHR Workflow Integrations</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  <SynapticText text="Seamlessly integrate" delay={100} />
                </span>
                <span className="block text-white mt-2">
                  <SynapticText text="with your existing EHR systems" delay={800} />
                </span>
              </h1>
              
              <motion.p 
                className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                Built from real-world healthcare data spanning millions of patient encounters, our solution adapts to your unique organizational needs, medical specialty requirements, provider preferences, and regional healthcare patterns.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
              >
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Request Integration Demo
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/case-studies"
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-lg font-semibold
                           hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  View Case Studies
                </RouterLink>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Integration Features */}
        <section className="py-24 bg-gradient-to-b from-background to-background/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Enterprise-Grade EHR Integration
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our platform seamlessly connects with your existing healthcare infrastructure, 
                providing intelligent automation without disrupting established workflows.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {integrationFeatures.map((feature, index) => (
                <JourneyCard
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  number={feature.number}
                  index={index}
                  shouldStartTyping={typingCardIndex === index}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Trusted by Leading Healthcare Organizations
              </h3>
              <p className="text-muted-foreground">
                Seamlessly integrated with the platforms you already use
              </p>
            </motion.div>

            <div className="flex justify-center items-center gap-12 opacity-60 hover:opacity-100 transition-opacity duration-300">
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
                className="filter grayscale hover:grayscale-0 transition-all duration-300"
              >
                <img 
                  src="/logos/microsoft.svg" 
                  alt="Microsoft" 
                  className="h-12 w-auto"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
                className="filter grayscale hover:grayscale-0 transition-all duration-300"
              >
                <img 
                  src="/logos/google.svg" 
                  alt="Google" 
                  className="h-12 w-auto"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-gradient-to-b from-background/50 to-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Proven Results Across Healthcare Organizations
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our EHR integrations deliver measurable improvements in efficiency, 
                security, and provider satisfaction.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <FloatingIslandCard
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-foreground mb-6">
                Ready to Transform Your EHR Integration?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of healthcare providers who have streamlined their workflows 
                with our intelligent EHR integration platform.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Schedule Integration Consultation
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/white-papers-research"
                  className="group border-2 border-primary text-primary px-8 py-4 rounded-lg font-semibold
                           hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Download Integration Guide
                </RouterLink>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default EHRWorkflowIntegrations;